import React, { lazy } from 'react';
import { Phone, AlertTriangle, Snowflake, Droplets, Wind, Calendar, Layers } from 'lucide-react';
const PHONE = '586-203-3701';
interface Sign {
  Icon: typeof AlertTriangle;
  title: string;
  body: string;
  image: string;
  fallback?: string;
  imageAlt: string;
}
const SIGNS: Sign[] = [{
  Icon: AlertTriangle,
  title: 'Curling or Missing Shingles',
  body: 'Curled edges or bald spots mean your roof has reached the end of its life.',
  image: 'https://images.unsplash.com/photo-1632759145351-1d592919f522?w=900&q=80&auto=format&fit=crop',
  fallback: 'https://images.unsplash.com/photo-1605276374104-dee2a0ed3cd6?w=900&q=80&auto=format&fit=crop',
  imageAlt: 'Damaged asphalt shingle roof showing curled and missing shingles'
}, {
  Icon: Droplets,
  title: 'Leaks or Water Stains',
  body: 'Brown ceiling stains or active drips mean water is getting past the shingles.',
  image: 'https://images.unsplash.com/photo-1604762524889-3e2fcc145683?w=900&q=80&auto=format&fit=crop',
  fallback: 'https://images.unsplash.com/photo-1558618666-fcd25c85cd64?w=900&q=80&auto=format&fit=crop',
  imageAlt: 'Water stain on a ceiling caused by a leaking roof'
}, {
  Icon: Snowflake,
  title: 'Ice Dams in Winter',
  body: 'Common across Michigan — usually a sign of ventilation and insulation problems.',
  image: 'https://images.unsplash.com/photo-1612892483236-52d32a0e0ac1?w=900&q=80&auto=format&fit=crop',
  fallback: 'https://images.unsplash.com/photo-1517299321609-52687d1bc55a?w=900&q=80&auto=format&fit=crop',
  imageAlt: 'Icicles and ice dam forming along the edge of a snow-covered Michigan roof'
}, {
  Icon: Wind,
  title: 'Storm or Hail Damage',
  body: "Even one strong Michigan storm can cause hidden damage that's easy to miss.",
  image: 'https://images.leadconnectorhq.com/image/f_webp/q_80/r_1200/u_https://assets.cdn.filesafe.space/RMzNb2TUk9Xj1mj8RDD5/media/69f22a90590487fe57c8cfc7.jpg',
  imageAlt: 'Storm and hail damage on a residential shingle roof inspected by Moceri'
}, {
  Icon: Layers,
  title: 'Granules in Gutters',
  body: 'Excessive granule loss in your gutters means your shingles are wearing down.',
  image: 'https://images.unsplash.com/photo-1605152276897-4f618f831968?w=900&q=80&auto=format&fit=crop',
  fallback: 'https://images.unsplash.com/photo-1558618666-fcd25c85cd64?w=900&q=80&auto=format&fit=crop',
  imageAlt: 'Gutter on a residential home filled with debris and shingle granules'
}, {
  Icon: Calendar,
  title: 'Roof Is 20+ Years Old',
  body: 'Most asphalt shingle roofs need replacement after two decades of Michigan weather.',
  image: 'https://images.unsplash.com/photo-1568605114967-8130f3a36994?w=900&q=80&auto=format&fit=crop',
  fallback: 'https://images.unsplash.com/photo-1518780664697-55e3ad937233?w=900&q=80&auto=format&fit=crop',
  imageAlt: 'Older suburban home with an aging asphalt shingle roof'
}];
export function RoofReplacement() {
  return <section className="bg-slate-50 py-20 sm:py-24" data-id="element-272">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-273">
        <div className="text-center max-w-3xl mx-auto mb-14" data-id="element-274">
          <span className="inline-block text-[#2DB8E0] font-bold uppercase tracking-wider text-sm mb-4" data-id="element-275">
            Roof Replacement
          </span>
          <h2 className="font-display italic text-4xl sm:text-5xl text-[#1a2332] leading-tight mb-4" data-id="element-276">
            Does Your Roof Need to Be Replaced?
          </h2>
          <p className="text-lg text-slate-600" data-id="element-277">
            If you're seeing any of these warning signs, don't wait. A free
            inspection from Moceri will give you a straight answer — no
            high-pressure sales tactics.
          </p>
        </div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 mb-12" data-id="element-278">
          {SIGNS.map(({
          Icon,
          title,
          body,
          image,
          fallback,
          imageAlt
        }) => <div key={title} className="group bg-white rounded-xl overflow-hidden border border-slate-100 shadow-sm hover:shadow-lg hover:-translate-y-1 transition-all duration-300" data-id="element-279">
              {/* Image */}
              <div className="relative h-48 overflow-hidden bg-slate-200" data-id="element-280">
                <img src={image} alt={imageAlt} loading="lazy" className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" onError={e => {
              if (fallback) {
                ;
                (e.currentTarget as HTMLImageElement).src = fallback;
              }
            }} data-id="element-281" />
                {/* Dark gradient for badge legibility */}
                <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-transparent" data-id="element-282" />
                {/* Icon badge */}
                <div className="absolute top-4 left-4 w-11 h-11 rounded-full bg-white shadow-md flex items-center justify-center" data-id="element-283">
                  <Icon className="w-5 h-5 text-[#2DB8E0]" data-id="element-284" />
                </div>
              </div>

              {/* Content */}
              <div className="p-6" data-id="element-285">
                <h3 className="font-bold text-[#1a2332] text-lg mb-2" data-id="element-286">
                  {title}
                </h3>
                <p className="text-slate-600 text-sm leading-relaxed" data-id="element-287">{body}</p>
              </div>
            </div>)}
        </div>

        <div className="text-center" data-id="element-288">
          <a href={`tel:${PHONE.replace(/-/g, '')}`} className="inline-flex items-center gap-3 bg-[#1a2332] hover:bg-[#2d3a4d] text-white font-bold text-base sm:text-lg px-6 sm:px-8 py-4 rounded-full transition-colors shadow-lg" data-id="element-289">
            <Phone className="w-5 h-5 text-[#2DB8E0]" data-id="element-290" />
            Call Now for a Free Inspection: {PHONE}
          </a>
        </div>
      </div>
    </section>;
}